import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { X, Scale, Trash2 } from 'lucide-react';
import { useCompare, removeCompare, clearCompare, MAX_COMPARE, deriveBrand } from '@/lib/productStore';

const CATEGORY_LABELS = {
  sim_rigs: 'SIM RIGS',
  steering_wheels: 'WHEELS',
  pedals: 'PEDALS',
  motion_platforms: 'MOTION',
  monitors: 'DISPLAYS',
  accessories: 'BRACKETS',
  gaming_computers: 'COMPUTERS',
  wheel_base: 'WHEEL BASE',
};

const ATTRS = [
  { key: 'price', label: 'PRICE', render: (p) => `£${Number(p.price || 0).toFixed(2)}` },
  { key: 'brand', label: 'BRAND', render: (p) => deriveBrand(p) || '—' },
  { key: 'platform', label: 'PLATFORM', render: (p) => (p.platform || '—').toUpperCase() },
  { key: 'stock', label: 'STOCK', render: (p) => (p.in_stock === false ? 'Out of stock' : 'In stock') },
  { key: 'category', label: 'CATEGORY', render: (p) => CATEGORY_LABELS[p.category] || (p.category || '—').toUpperCase() },
  { key: 'specs', label: 'SPECS', render: (p) => (p.specs ? <span className="whitespace-pre-line text-xs leading-relaxed">{p.specs}</span> : '—') },
];

export default function CompareBar() {
  const compare = useCompare();
  const [open, setOpen] = useState(false);

  const { data: products = [] } = useQuery({
    queryKey: ['compare-products', compare.join(',')],
    queryFn: async () => {
      if (!compare.length) return [];
      const all = await base44.entities.Product.list();
      return compare.map((id) => all.find((p) => p.id === id)).filter(Boolean);
    },
    enabled: compare.length > 0,
  });

  if (compare.length === 0) return null;

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-white/10 bg-[#111115]/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-3 flex items-center gap-3">
          <Scale className="w-4 h-4 text-primary shrink-0" />
          <span className="text-white/40 text-xs font-bold tracking-widest shrink-0 hidden sm:block">COMPARE</span>
          <div className="flex items-center gap-2 overflow-x-auto flex-1 scrollbar-hide">
            {products.map((p) => (
              <div key={p.id} className="flex items-center gap-2 bg-[#0d0d0f] border border-white/10 pl-1.5 pr-2 py-1 shrink-0">
                <div className="w-7 h-7 bg-white/5 overflow-hidden shrink-0">
                  {p.image_url ? <img src={p.image_url} alt="" className="w-full h-full object-cover" /> : null}
                </div>
                <span className="text-white/70 text-xs font-bold max-w-[140px] truncate">{p.name}</span>
                <button onClick={() => removeCompare(p.id)} className="text-white/30 hover:text-red-400 transition-colors">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            {compare.length < MAX_COMPARE && (
              <span className="text-white/20 text-[10px] font-bold tracking-widest shrink-0 px-2">
                ADD UP TO {MAX_COMPARE - compare.length} MORE
              </span>
            )}
          </div>
          <button onClick={() => setOpen(true)} disabled={compare.length < 2}
            className="bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest px-5 py-2.5 transition-colors disabled:opacity-40 shrink-0">
            COMPARE ({compare.length})
          </button>
          <button onClick={clearCompare} className="text-white/40 hover:text-white shrink-0 transition-colors" title="Clear compare" aria-label="Clear compare">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm p-4 overflow-auto" onClick={() => setOpen(false)}>
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-5xl mx-auto my-8 bg-[#111115] border border-white/10">
              <div className="flex items-center justify-between p-5 border-b border-white/5 sticky top-0 bg-[#111115] z-10">
                <h3 className="text-white font-black text-lg uppercase">Compare Products</h3>
                <button onClick={() => setOpen(false)} className="text-white/40 hover:text-white transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <tbody>
                    {/* Header row: image + name */}
                    <tr>
                      <td className="w-28 p-3 align-top"></td>
                      {products.map((p) => (
                        <td key={p.id} className="p-3 align-top border-l border-white/5 min-w-[180px]">
                          <Link to={`/shop/${p.custom_slug || p.id}`} onClick={() => setOpen(false)} className="group block">
                            <div className="aspect-square bg-[#0d0d0f] overflow-hidden mb-2 flex items-center justify-center p-2">
                              {p.image_url ? (
                                <img src={p.image_url} alt={p.name} className="max-w-full max-h-full object-contain" />
                              ) : (
                                <span className="text-white/10 text-[10px]">NO IMAGE</span>
                              )}
                            </div>
                            <p className="text-white font-bold text-sm leading-snug line-clamp-2 group-hover:text-primary transition-colors">{p.name}</p>
                          </Link>
                        </td>
                      ))}
                    </tr>
                    {ATTRS.map((attr) => (
                      <tr key={attr.key} className="border-t border-white/5">
                        <td className="p-3 text-white/40 text-[10px] font-bold tracking-widest align-top">{attr.label}</td>
                        {products.map((p) => (
                          <td key={p.id} className="p-3 text-white/80 text-sm align-top border-l border-white/5">{attr.render(p)}</td>
                        ))}
                      </tr>
                    ))}
                    <tr className="border-t border-white/5">
                      <td className="p-3"></td>
                      {products.map((p) => (
                        <td key={p.id} className="p-3 border-l border-white/5">
                          <button onClick={() => removeCompare(p.id)} className="inline-flex items-center gap-1 text-[10px] font-bold tracking-widest text-red-400/80 hover:text-red-400 transition-colors">
                            <X className="w-3 h-3" /> REMOVE
                          </button>
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}